import styled from "@emotion/styled";
import { useParams } from "react-router-dom";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import {
  type Exception,
  type ExceptionWithInteractions,
  type InteractionLog,
  type ParsedSession,
  type Session as SessionType,
  type SessionLog,
} from "../../utils/types";

import { customFirestore } from "@/firebase";
import { CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import Info from "./info";
import Content from "./content";
import SessionAutoReview from "./auto-review";
import { useAtomValue } from "jotai";
import { companyDataAtom } from "@/routes/test-run/test-run-atoms";
import { AuthContext } from "@/auth/AuthContext";
import { LinkOff } from "@mui/icons-material";

function useSessionData(sessionId: string | undefined | null) {
  const { user, logOut, setForceLogout } = useContext<any>(AuthContext);

  const [sessionState, setSessionState] = useState<{
    loading: boolean;
    error: any;
    data: SessionType | null;
  }>({ loading: !!sessionId, error: null, data: null });

  const [exceptionsState, setExceptionsState] = useState<{
    loading: boolean;
    error: any;
    data: Exception[] | null;
  }>({ loading: !!sessionId, error: null, data: null });

  useEffect(() => {
    if (!sessionId) {
      setSessionState({ loading: false, error: null, data: null });
      setExceptionsState({ loading: false, error: null, data: null });
      return;
    }

    setSessionState({ loading: true, error: null, data: null });
    setExceptionsState({ loading: true, error: null, data: null });

    const unsubscribe = onSnapshot(
      doc(customFirestore, "sessions", sessionId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setSessionState((prev) => ({
            ...prev,
            loading: false,
            error: new Error("Session doesn't exist"),
          }));
          return;
        }
        setSessionState((prev) => ({
          ...prev,
          loading: false,
          data: {
            ...(snapshot.data() as SessionType),
            id: sessionId,
            debugOnly: snapshot.data().debugOnly ?? false,
            testId: snapshot.data().testId,
            cachingMode: snapshot.data().cachingMode,
            deviceConfig: snapshot.data().deviceConfig,
          },
        }));
      },
      (error) => {
        if (error.code === "permission-denied") {
          setForceLogout(true);
          logOut();
        }
        setSessionState((prev) => ({
          ...prev,
          loading: false,
          error,
        }));
      }
    );
    return () => unsubscribe();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !sessionState.data || !user) return;

    const thirtyDaysAgo = Date.now() - 120 * 24 * 60 * 60 * 1000;
    const organisationId = sessionState.data.organisationId;

    const firebaseQuery = query(
      collection(customFirestore, `exceptions`),
      where("sessionId", "==", sessionId),
      where("organisationId", "==", organisationId),
      ...(user.isAnonymous ? [where("timestamp", ">=", thirtyDaysAgo)] : []),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(
      firebaseQuery,
      (snapshots) => {
        setExceptionsState((prev) => ({
          ...prev,
          loading: false,
          data: snapshots.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<Exception, "id">),
          })),
        }));
      },
      (error) => {
        console.error("Error fetching exceptions:", error);
        setExceptionsState((prev) => ({
          ...prev,
          loading: false,
          error,
        }));
      }
    );
    return () => unsubscribe();
  }, [sessionState.data, sessionId, user]);

  const parsedData: ParsedSession | undefined = useMemo(() => {
    if (!sessionState.data || !exceptionsState.data) return undefined;

    const processedSession: ParsedSession = {
      ...sessionState.data,
      logs: [],
    };

    // interactionLogs land in Firestore in background-task completion order,
    // not necessarily the order the steps actually occurred in (see MB-549),
    // so re-sort by timestamp before rendering.
    const interactionLogs = [...(sessionState.data.interactionLogs || [])].sort(
      (a: SessionLog, b: SessionLog) => {
        if (a.timestamp < b.timestamp) return -1;
        if (a.timestamp > b.timestamp) return 1;
        return 0;
      }
    );
    const exceptionMap = new Map();
    exceptionsState.data.forEach((exception) => {
      if (!exception.debugOnly) {
        exceptionMap.set(exception.id, exception);
      }
    });

    const processedLogs: ExceptionWithInteractions[] = [];
    let currentException: Exception | null = null;
    let currentInteractionLogs: InteractionLog[] = [];

    interactionLogs.forEach((log: InteractionLog) => {
      const exceptionDocId = log.exceptionDocId;
      const exception = exceptionDocId
        ? exceptionMap.get(exceptionDocId) || null
        : null;

      if (exception) {
        if (currentException && currentException.id === exception.id) {
          currentInteractionLogs.push(log);
        } else {
          if (currentInteractionLogs.length > 0) {
            processedLogs.push({
              exception: currentException,
              interactionLogs: currentInteractionLogs,
            });
          }
          currentException = exception;
          currentInteractionLogs = [log];
        }
      } else {
        if (currentInteractionLogs.length > 0) {
          processedLogs.push({
            exception: currentException,
            interactionLogs: currentInteractionLogs,
          });
          currentInteractionLogs = [];
          currentException = null;
        }
        processedLogs.push({
          exception: null,
          interactionLogs: [log],
        });
      }
    });

    if (currentInteractionLogs.length > 0) {
      processedLogs.push({
        exception: currentException,
        interactionLogs: currentInteractionLogs,
      });
    }

    processedSession.logs = processedLogs;
    return processedSession;
  }, [exceptionsState.data, sessionState.data]);

  const isLoading =
    !!sessionId &&
    (sessionState.loading ||
      exceptionsState.loading ||
      (!sessionState.data && !sessionState.error));

  return {
    loading: isLoading,
    error: sessionState.error || exceptionsState.error,
    sessionData: sessionState.data,
    parsedData,
  };
}

const Session = () => {
  const { sessionId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useContext<any>(AuthContext);
  const isSuperUser = user?.role === "superUser";
  const { data: companyData } = useAtomValue(companyDataAtom(user?.companyId));
  const reviewEnabled =
    companyData?.settings?.enableSdkSessionsAutoReview === true;
  // Review UI is superuser-only AND only for orgs where the feature is enabled.
  const showReviewUi = isSuperUser && reviewEnabled;

  const current = useSessionData(sessionId);
  const baseline = useSessionData(current.sessionData?.baselineSessionId);

  const baselineSessionId = current.sessionData?.baselineSessionId;

  if (current.loading) {
    return (
      <SpinnerWrapper>
        <CircularProgress />
      </SpinnerWrapper>
    );
  }

  return (
    <PageOuterWrapper>
      <TabsBar>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "1rem",
            },
          }}
        >
          <Tab label="Current Session" />
          <Tab label="Baseline Session" />
          {showReviewUi && <Tab label="Auto-review" />}
        </Tabs>
      </TabsBar>

      <ContentArea style={{ display: activeTab === 0 ? undefined : "none" }}>
        {current.parsedData && <Content session={current.parsedData} />}
      </ContentArea>

      <ContentArea style={{ display: activeTab === 1 ? undefined : "none" }}>
        {!baselineSessionId ? (
          <ContentPlaceholder>
            <NoBaselineWrapper>
              <LinkOff
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No baseline session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This session does not have a baseline session associated with
                it.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Baseline sessions are used as cache references for the current
                session.
              </Typography>
            </NoBaselineWrapper>
          </ContentPlaceholder>
        ) : baseline.loading ? (
          <ContentPlaceholder>
            <ContentSpinnerWrapper>
              <CircularProgress />
            </ContentSpinnerWrapper>
          </ContentPlaceholder>
        ) : baseline.error ? (
          <ContentPlaceholder>
            <NoBaselineWrapper>
              <Typography variant="h6" color="error" gutterBottom>
                Failed to load baseline session
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Could not fetch data for baseline session {baselineSessionId}.
              </Typography>
            </NoBaselineWrapper>
          </ContentPlaceholder>
        ) : (
          baseline.parsedData && <Content session={baseline.parsedData} />
        )}
      </ContentArea>

      {showReviewUi && (
        <ContentArea style={{ display: activeTab === 2 ? undefined : "none" }}>
          {current.sessionData && (
            <SessionAutoReview session={current.sessionData} />
          )}
        </ContentArea>
      )}

      <InfoArea>
        {activeTab === 1 && baseline.sessionData ? (
          <Info session={baseline.sessionData} isBaseline />
        ) : (
          current.sessionData && <Info session={current.sessionData} />
        )}
      </InfoArea>
    </PageOuterWrapper>
  );
};

const SpinnerWrapper = styled.div`
  display: grid;
  place-content: center;
  height: 100%;
  flex: 1;
`;

const PageOuterWrapper = styled.div`
  display: grid;
  grid-template-columns: 5fr 2.5fr;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  padding: 12px;
  gap: 0 12px;
`;

const TabsBar = styled.div`
  grid-column: 1;
  grid-row: 1;
  background-color: rgb(232, 232, 232);
  padding: 6px 16px 0;
  border-radius: 8px 8px 0 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: none;
`;

const ContentArea = styled.div`
  grid-column: 1;
  grid-row: 2;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: rgb(232, 232, 232);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-top: none;
  border-radius: 0 0 8px 8px;

  > * {
    flex: 1;
    min-height: 0;
  }
`;

const InfoArea = styled.div`
  grid-column: 2;
  grid-row: 1 / -1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > * {
    flex: 1;
    min-height: 0;
  }
`;

const NoBaselineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  text-align: center;
`;

const ContentSpinnerWrapper = styled.div`
  display: grid;
  place-content: center;
`;

const ContentPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
`;

export default Session;
